import React, { useCallback, useEffect, useState } from 'react';

import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

import { StackNavigationProp } from '@react-navigation/stack';

import { LANGUAGE_KEYS } from 'config/languageKeys';

import { SCREEN } from 'router/screens';
import { RouteNavigationParams } from 'router/types';

import { selectLayoutConfig } from 'store/config/selectors';
import { logout } from 'store/user/actions';

import { DidomiRef } from 'services/consents-management/DidomiSDK/utils/createDidomiRef.ts';
import { RetryController } from 'services/opf-retry-mechanism';
import { isErrorNeedsRetry } from 'services/opf-retry-mechanism/utils.ts';

import DialogView from 'components/Dialog';

import styles from './styles';

const {
    logout: {
        SCREEN_ALERT_LOGOUT_HEADER,
        SCREEN_ALERT_LOGOUT_SUBHEADER,
        SCREEN_ALERT_CANCEL_BUTTON,
        SCREEN_ALERT_LOGOUT_BUTTON,
    },
} = LANGUAGE_KEYS;

type Props = {
    navigation: StackNavigationProp<
        RouteNavigationParams,
        SCREEN.SETTINGS_LOGOUT
    >;
};

const LogoutPage = ({ navigation }: Props) => {
    const dispatch = useDispatch();
    const { t } = useTranslation();

    const { primary } = useSelector(selectLayoutConfig);

    const handleLogout = useCallback(async () => {
        const retryLogoutAction = () =>
            new Promise<void>((resolve, reject) => {
                dispatch(
                    logout(navigation, {
                        onSuccess: () => {
                            DidomiRef.current?.logout();
                            resolve();
                        },
                        onFailure: reject,
                    }),
                );
            });

        dispatch(
            logout(navigation, {
                onSuccess: () => {
                    DidomiRef.current?.logout();
                },
                onFailure(error) {
                    if (isErrorNeedsRetry(error)) {
                        RetryController.showVitalDialog({
                            actionButtonTitle: LANGUAGE_KEYS.CLOSE_BUTTON,
                            actionButtonOnPress: () => {
                                navigation.goBack();
                                RetryController.hideVitalDialog();
                            },
                        });
                        RetryController.registerRetryCallback(
                            retryLogoutAction,
                            { maxRetryCount: 5, retryDuration: 5 * 60 * 1000 },
                        );
                        RetryController.executeRetry();
                    }
                },
            }),
        );
    }, [dispatch, navigation]);

    const handleCancelLogoutDialog = useCallback(() => {
        navigation.goBack();
    }, [navigation]);

    const [vitalErrorVisible, setVitalErrorVisible] = useState(false);

    useEffect(() => {
        const unsubscribe = RetryController.subscribe(setVitalErrorVisible);

        return () => {
            unsubscribe();
        };
    }, []);

    return (
        <DialogView
            style={styles.dialog}
            title={SCREEN_ALERT_LOGOUT_HEADER}
            description={SCREEN_ALERT_LOGOUT_SUBHEADER}
            actionButtonTitle={SCREEN_ALERT_LOGOUT_BUTTON}
            actionButtonAccessibilityLabel={t(SCREEN_ALERT_LOGOUT_BUTTON)}
            actionButtonOnPress={handleLogout}
            cancelButtonTitle={SCREEN_ALERT_CANCEL_BUTTON}
            cancelButtonAccessibilityLabel={t(SCREEN_ALERT_CANCEL_BUTTON)}
            cancelButtonOnPress={handleCancelLogoutDialog}
            cancelButtonFillColor={primary}
            actionButtonStyle={styles.button}
            actionButtonTextStyle={styles.actionButtonText}
            cancelButtonStyle={styles.button}
            cancelButtonTextStyle={styles.cancelButtonText}
            actionButtonBordered
            visible={!vitalErrorVisible}
            revertButtons
        />
    );
};

export default LogoutPage;
